// Minimal collision helpers — circle-vs-AABB resolver + line-vs-AABB for bullets.
// All positions are in world XZ plane; Y is ignored.

export function circleVsAabb(cx, cz, r, minX, minZ, maxX, maxZ) {
  const nx = Math.max(minX, Math.min(cx, maxX));
  const nz = Math.max(minZ, Math.min(cz, maxZ));
  const dx = cx - nx;
  const dz = cz - nz;
  const d2 = dx * dx + dz * dz;
  if (d2 > r * r) return null;
  let penX = 0, penZ = 0;
  if (d2 > 0.0001) {
    const d = Math.sqrt(d2);
    penX = (dx / d) * (r - d);
    penZ = (dz / d) * (r - d);
  } else {
    // Center is inside the box — push out along smallest axis
    const left = cx - minX, right = maxX - cx;
    const top = cz - minZ, bottom = maxZ - cz;
    const mx = Math.min(left, right);
    const mz = Math.min(top, bottom);
    if (mx < mz) {
      penX = (left < right ? -mx : mx) + Math.sign(left < right ? -1 : 1) * r;
    } else {
      penZ = (top < bottom ? -mz : mz) + Math.sign(top < bottom ? -1 : 1) * r;
    }
  }
  return { x: penX, z: penZ };
}

// Resolve a circle moving from (x,z) against a list of AABB walls.
// Walls is an array of {minX,minZ,maxX,maxZ}. Returns the corrected {x,z}.
export function resolveCircleWalls(x, z, r, walls) {
  // Two passes is plenty for small wall counts
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 0; i < walls.length; i++) {
      const w = walls[i];
      const pen = circleVsAabb(x, z, r, w.minX, w.minZ, w.maxX, w.maxZ);
      if (pen) { x += pen.x; z += pen.z; }
    }
  }
  return { x, z };
}

export function circleVsCircle(ax, az, ar, bx, bz, br) {
  const dx = ax - bx, dz = az - bz;
  const d2 = dx * dx + dz * dz;
  const rs = ar + br;
  return d2 <= rs * rs;
}

// Segment from (sx,sz) to (ex,ez) against axis-aligned boxes; returns nearest hit t [0..1] or null.
export function segmentVsAabbs(sx, sz, ex, ez, walls) {
  let bestT = 1.0;
  let hit = false;
  const dx = ex - sx, dz = ez - sz;
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i];
    const t = sweepSegAabb(sx, sz, dx, dz, w);
    if (t !== null && t < bestT) { bestT = t; hit = true; }
  }
  return hit ? bestT : null;
}

function sweepSegAabb(sx, sz, dx, dz, w) {
  let tmin = 0, tmax = 1;
  // X slab
  if (Math.abs(dx) < 1e-8) {
    if (sx < w.minX || sx > w.maxX) return null;
  } else {
    const tx1 = (w.minX - sx) / dx;
    const tx2 = (w.maxX - sx) / dx;
    const lo = Math.min(tx1, tx2), hi = Math.max(tx1, tx2);
    tmin = Math.max(tmin, lo);
    tmax = Math.min(tmax, hi);
    if (tmin > tmax) return null;
  }
  // Z slab
  if (Math.abs(dz) < 1e-8) {
    if (sz < w.minZ || sz > w.maxZ) return null;
  } else {
    const tz1 = (w.minZ - sz) / dz;
    const tz2 = (w.maxZ - sz) / dz;
    const lo = Math.min(tz1, tz2), hi = Math.max(tz1, tz2);
    tmin = Math.max(tmin, lo);
    tmax = Math.min(tmax, hi);
    if (tmin > tmax) return null;
  }
  return tmin >= 0 && tmin <= 1 ? tmin : null;
}
